// @spec AST-STR-049
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AssetGovernanceProfileCard,
  CreateCustomFieldDialog,
  CustomFieldList,
  DeleteCustomFieldDialog,
  EditCustomFieldDialog,
} from '@/features/settings/assets';
import type { AssetCustomField } from '@/features/settings/assets';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

const searchSchema = z.object({
  tab: z.enum(['fields', 'governance']).optional().default('fields'),
});

export const Route = createFileRoute('/_authenticated/settings/asset-custom-fields')({
  validateSearch: searchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.assetCustomFields'),
  }),
  component: AssetCustomFieldsPage,
});

/**
 * Asset custom fields management page.
 *
 * Shows list of custom fields and allows admins to create, edit, and delete them.
 */
function AssetCustomFieldsPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isLoadingRole } = useOrgRole();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingField, setEditingField] = useState<AssetCustomField | null>(null);
  const [deletingField, setDeletingField] = useState<AssetCustomField | null>(null);

  useEffect(() => {
    if (!isLoadingRole && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isLoadingRole, isAdmin, navigate]);

  if (!isLoadingRole && !isAdmin) {
    return null;
  }

  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  if (isLoadingRole || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <header>
            <Skeleton className="h-9 w-48" />
            <Skeleton className="mt-2 h-5 w-80" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const handleTabChange = (nextTab: string) => {
    if (nextTab !== 'fields' && nextTab !== 'governance') {
      return;
    }
    void navigate({
      search: (prev) => ({ ...prev, tab: nextTab }),
      replace: true,
    });
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">
            {t('assetCustomFieldsPage.title')}
          </h1>
          <p className="mt-1 text-muted-foreground">{t('assetCustomFieldsPage.description')}</p>
        </header>

        <Tabs value={search.tab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList>
            <TabsTrigger value="fields">{t('assetCustomFieldsPage.customFieldsTab')}</TabsTrigger>
            <TabsTrigger value="governance">
              {t('assetCustomFieldsPage.assetGuidelinesTab')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="fields">
            <CustomFieldList
              onCreateClick={() => {
                setIsCreateOpen(true);
              }}
              onEditClick={(field) => {
                setEditingField(field);
              }}
              onDeleteClick={(field) => {
                setDeletingField(field);
              }}
              isAdmin={isAdmin}
            />
          </TabsContent>

          <TabsContent value="governance">
            <AssetGovernanceProfileCard isAdmin={isAdmin} />
          </TabsContent>
        </Tabs>

        {/* Create Dialog */}
        <CreateCustomFieldDialog
          open={isCreateOpen}
          onOpenChange={(open) => {
            if (!open) {
              setIsCreateOpen(false);
            }
          }}
        />

        {/* Edit Dialog */}
        <EditCustomFieldDialog
          field={editingField}
          open={editingField !== null}
          onOpenChange={(open) => {
            if (!open) {
              setEditingField(null);
            }
          }}
        />

        {/* Delete Dialog */}
        <DeleteCustomFieldDialog
          field={deletingField}
          open={deletingField !== null}
          onOpenChange={(open) => {
            if (!open) {
              setDeletingField(null);
            }
          }}
        />
      </div>
    </div>
  );
}
